import attachmentType from 'owa-service/lib/factory/attachmentType';
import referenceAttachment from 'owa-service/lib/factory/referenceAttachment';
import { AttachmentConstants } from 'owa-attachment-model-store';
import { is3SFeatureEnabled } from 'owa-mail-feature-flags';
import {
    SubstrateSearchSuggestionsResponse,
    SuggestionSet,
    Suggestion,
    SubstrateSearchSuggestionsResponseTextSuggestion,
    KeywordSuggestion,
    SuggestionKind,
    SubstrateSearchSuggestionsResponsePeopleSuggestion,
    PeopleSuggestion,
    SubstrateSearchSuggestionsResponseMessageSuggestion,
    MessageSuggestion,
    SubstrateSearchSuggestionsResponseAttachmentSuggestion,
    FileSuggestion,
} from 'owa-search-service';
import {
    MAX_SERVER_SUGGESTION_OF_KIND,
    HIGHLIGHT_REGEX,
} from 'owa-mail-search/lib/searchConstants';
import { trace } from 'owa-trace';

/**
 * Parses 3S suggestions response and converts it to a SuggestionSet.
 */
export default function convertSubstrateSuggestionsResponseToSuggestionSet(
    response: SubstrateSearchSuggestionsResponse
): SuggestionSet {
    if (!response || !response.Groups) {
        throw new Error('Substrate suggestions response "Groups" should not be null.');
    }

    // Array of parsed suggestions to return.
    const suggestions: Suggestion[] = [];

    // Counts of how many suggestions of each type are in array to return.
    let keywordSuggestionCount = 0;
    let peopleSuggestionCount = 0;

    // Iterate over groups of suggestions and convert to known suggestion types.
    for (const group of response.Groups) {
        switch (group.Type) {
            case 'Text':
                {
                    // Determine how many more keyword suggestions we should fetch.
                    const maxSuggestionsOfKind =
                        MAX_SERVER_SUGGESTION_OF_KIND - keywordSuggestionCount;

                    /**
                     * Get the first "maxSuggestionsOfKind" remaining keyword
                     * suggestions (or none if we are over max).
                     */
                    const convertedKeywords = group.Suggestions.map(
                        convertToKeywordSuggestion
                    ).slice(0, maxSuggestionsOfKind > 0 ? maxSuggestionsOfKind : 0);

                    // Add to suggestions array to return.
                    suggestions.push(...convertedKeywords);

                    // Increment count of fetched keyword suggestions.
                    keywordSuggestionCount += convertedKeywords.length;
                }
                break;
            case 'People':
                {
                    if (!validatePeopleSuggestions(group.Suggestions)) {
                        continue;
                    }

                    // Determine how many more people suggestions we should fetch.
                    const maxSuggestionsOfKind =
                        MAX_SERVER_SUGGESTION_OF_KIND - peopleSuggestionCount;

                    /**
                     * Get the first "maxSuggestionsOfKind" remaining people
                     * suggestions (or none if we are over max).
                     */
                    const convertedPeople = group.Suggestions.map(convertToPeopleSuggestion).slice(
                        0,
                        maxSuggestionsOfKind > 0 ? maxSuggestionsOfKind : 0
                    );

                    // Add to suggestions array to return.
                    suggestions.push(...convertedPeople);

                    // Increment count of fetched people suggestions.
                    peopleSuggestionCount += convertedPeople.length;
                }
                break;
            case 'Message':
                if (is3SFeatureEnabled('sea-emailSuggestions')) {
                    const convertedMessageSuggestions = group.Suggestions.map(
                        convertToMessageSuggestion
                    );

                    suggestions.push(...convertedMessageSuggestions);
                }
                break;
            case 'Attachment':
                if (is3SFeatureEnabled('sea-filesSuggestions')) {
                    const convertedFileSuggestions = group.Suggestions.map(convertToFileSuggestion);
                    suggestions.push(...convertedFileSuggestions);
                }
                break;
        }
    }

    return {
        Suggestions: suggestions,
        IsComplete: true,
        TraceId: response.Instrumentation.TraceId,
    };
}

const validatePeopleSuggestions = (
    suggestions: SubstrateSearchSuggestionsResponsePeopleSuggestion[]
): boolean => {
    for (const suggestion of suggestions) {
        if (
            suggestion.DisplayName === undefined ||
            suggestion.Text === undefined ||
            suggestion.EmailAddresses === undefined
        ) {
            trace.error('People suggestions from 3S are not valid.');
            return false;
        }
    }

    return true;
};

function convertToKeywordSuggestion(
    suggestion: SubstrateSearchSuggestionsResponseTextSuggestion
): KeywordSuggestion {
    return {
        kind: SuggestionKind.Keywords,
        DisplayText: suggestion.Text,
        QueryText: suggestion.QueryText,
        ReferenceId: suggestion.ReferenceId,
        Attributes: suggestion.Attributes,
        Segments: suggestion.Segments,
    };
}

function convertToPeopleSuggestion(
    suggestion: SubstrateSearchSuggestionsResponsePeopleSuggestion
): PeopleSuggestion {
    return {
        kind: SuggestionKind.People,
        DisplayName: suggestion.DisplayName,
        HighlightedDisplayName: suggestion.Text,
        EmailAddresses: suggestion.EmailAddresses.map(x => x.replace(HIGHLIGHT_REGEX, '')),
        ReferenceId: suggestion.ReferenceId,
        Attributes: suggestion.Attributes,
        Source: 'qf',
        QueryText: suggestion.QueryText,
    };
}

function convertToMessageSuggestion(
    suggestion: SubstrateSearchSuggestionsResponseMessageSuggestion
): MessageSuggestion {
    return {
        kind: SuggestionKind.Message,
        Subject: suggestion.Subject,
        DisplayName:
            suggestion.From && suggestion.From.EmailAddress
                ? suggestion.From.EmailAddress.Name
                : '',
        ReferenceId: suggestion.ReferenceId,
        ItemId: suggestion.ItemId,
        ConversationId: suggestion.ConversationId,
        HasAttachments: suggestion.HasAttachments,
        DateTimeReceived: suggestion.DateTimeReceived,
    };
}

function convertToFileSuggestion(
    suggestion: SubstrateSearchSuggestionsResponseAttachmentSuggestion
) {
    const sender =
        suggestion.From && suggestion.From.EmailAddress ? suggestion.From.EmailAddress.Name : '';
    const fileSuggestion: FileSuggestion = {
        kind: SuggestionKind.Files,
        Name: suggestion.FileName,
        Subject: suggestion.Subject,
        Sender: sender,
        Attachment: getAttachmentTypeForFileSuggestion(suggestion),
        ReferenceId: suggestion.ReferenceId,
        ItemId: suggestion.ItemId.Id,
        ConversationId: suggestion.ConversationId.Id,
        AttachmentId: suggestion.AttachmentId ? suggestion.AttachmentId.Id : null,
        Extension: suggestion.FileExtension,
        HighlightedDisplayText: suggestion.Text,
        DateTimeReceived: suggestion.DateCreated,
    };

    return fileSuggestion;
}

function getAttachmentTypeForFileSuggestion(
    suggestion: SubstrateSearchSuggestionsResponseAttachmentSuggestion
) {
    if (suggestion.FileSourceType === 'ClassicAttachment') {
        return attachmentType({
            __type: AttachmentConstants.attachmentTypes.FILE_ATTACHMENT_TYPE,
            Name: suggestion.FileName,
            IsInline: false,
            AttachmentId: {
                Id: suggestion.AttachmentId ? suggestion.AttachmentId.Id : null,
            },
        });
    }

    return referenceAttachment({
        __type: AttachmentConstants.attachmentTypes.REFERENCE_ATTACHMENT_TYPE,
        Name: suggestion.FileName,
        AttachmentIsFolder: false,
        IsInline: false,
        ProviderType: AttachmentConstants.providers.ONE_DRIVE_PRO, // 3S only supports OneDrivePro for now
        AttachLongPathName: suggestion.AccessUrl,
        AttachmentId: {
            Id: suggestion.AttachmentId ? suggestion.AttachmentId.Id : null,
        },
    });
}
